import { toast as hotToast, type Toast as HotToast } from 'react-hot-toast'
import { XIcon } from 'lucide-react'
import { Progress as ProgressPrimitive } from '@base-ui/react/progress'

import { cn } from '@/components/new/lib/utils'
import { Alert } from '@/components/new/ui/alert'
import { Button } from '@/components/new/ui/button'

import type { CSSProperties } from 'react'

type CustomToastSeverity = 'error' | 'success'

const customToastSeverityClassNames: Record<CustomToastSeverity, string> = {
    error: 'bg-red-600/50',
    success: 'bg-green-600/50',
}

const getProgressStyle = ({ duration = 0 }: Pick<HotToast, 'duration'>): CSSProperties => {
    if (duration === Infinity) {
        return { animationName: 'none' }
    }

    const completionBuffer = Math.min(50, duration / 100)
    const animationDuration = Math.max(0, duration - completionBuffer)

    return {
        animationDuration: `${animationDuration}ms`,
    }
}

export const CustomToast = ({
    toast,
    message,
    severity,
}: {
    toast: HotToast
    message: string
    severity: CustomToastSeverity
}) => {
    if (toast.dismissed) {
        return null
    }

    return (
        <div className="dark:bg-background max-w-md rounded-lg">
            <Alert
                className="min-w-xs"
                severity={severity}
                footer={
                    <ProgressPrimitive.Root
                        value={100}
                        aria-hidden={true}
                        data-slot="progress"
                        className={cn('mx-0.5 flex flex-wrap gap-3')}
                    >
                        <ProgressPrimitive.Track
                            data-slot="progress-track"
                            className={cn('relative -mt-1 flex h-1 w-full items-center overflow-x-hidden rounded-b-lg')}
                        >
                            <ProgressPrimitive.Indicator
                                data-slot="progress-indicator"
                                className={cn(
                                    'toast-progress-indicator h-full',
                                    customToastSeverityClassNames[severity]
                                )}
                                style={getProgressStyle(toast)}
                            />
                        </ProgressPrimitive.Track>
                    </ProgressPrimitive.Root>
                }
            >
                <div className="items-top flex gap-2">
                    <div className="flex-1">{message}</div>
                    <Button variant="link" size="icon-xs" onClick={() => hotToast.dismiss(toast.id)}>
                        <XIcon className="size-3" />
                    </Button>
                </div>
            </Alert>
        </div>
    )
}
